/**
 * QboReportsTab — rapports lus en direct dans QuickBooks en ligne : états financiers (résultats, bilan, bilan + résultats, balance, grand livre),
 * taxes (sommaire TPS / TVQ par agence), clients et fournisseurs (âges des comptes, détail, soldes). Période, méthode, colonnes, tiers ; PDF du cabinet.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, FileDown, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, downloadPdf, errMsg, today } from "../../comptabilite/_shared";
import { Card } from "../../comptabilite/reports/_bits";
import QboReportTable from "./QboReportTable";
import { fmtDateTime, qboBase } from "./_qbo";

const dateCls = "border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white";
const COLUMNS = [["Total", "Total"], ["Month", "Par mois"], ["Quarter", "Par trimestre"], ["Year", "Par année"]];

function PartySelect({ masterId, kind, value, onChange }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get(`${qboBase(masterId)}/entities/${kind === "customer" ? "customers" : "vendors"}`).then((r) => { if (alive) setOpts(r.data.items || []); }).catch(() => setOpts([]));
    return () => { alive = false; };
  }, [masterId, kind]);
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${dateCls} max-w-xs`} data-testid="qbo-report-party">
      <option value="">{kind === "customer" ? "Tous les clients" : "Tous les fournisseurs"}</option>
      {opts.map((o) => <option key={o.qbo_id} value={o.qbo_id}>{o.name}</option>)}
    </select>
  );
}

export default function QboReportsTab({ masterId }) {
  const [sp, setSp] = useSearchParams();
  const [cat, setCat] = useState(null);
  const [p, setP] = useState({ start: "", end: today(), method: "Accrual", columns: "Total", party: "", agency: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`${qboBase(masterId)}/reports`).then((r) => { setCat(r.data); setP((x) => ({ ...x, start: r.data.fiscal_year_start })); }).catch((e) => toast.error(errMsg(e)));
  }, [masterId]);

  const reports = cat?.reports || [];
  const kind = reports.some((r) => r.id === sp.get("report")) ? sp.get("report") : "profit-loss";
  const def = reports.find((r) => r.id === kind);
  const groups = reports.reduce((acc, r) => { (acc[r.group] = acc[r.group] || []).push(r); return acc; }, {});
  const setKind = (id) => { const n = new URLSearchParams(sp); n.set("report", id); setSp(n, { replace: true }); setP((x) => ({ ...x, party: "" })); };

  const query = useCallback(() => {
    if (!def) return null;
    const q = { end: p.end };
    if (def.mode === "period") q.start = p.start;
    if (def.method) q.method = p.method;
    if (def.columns && p.columns !== "Total") q.columns = p.columns;
    if (def.party && p.party) q.party = p.party;
    if (kind === "tax-summary" && p.agency) q.agency = p.agency;
    return q;
  }, [def, p, kind]);

  const reqRef = useRef(0);
  const inflightRef = useRef("");
  const load = useCallback(async (refresh = false) => {
    const q = query();
    if (!q || !p.start || !p.end) return;
    const key = JSON.stringify({ kind, q, refresh });
    if (inflightRef.current === key) return;
    inflightRef.current = key;
    const id = ++reqRef.current;
    setLoading(true);
    try { const r = await api.get(`${qboBase(masterId)}/reports/${kind}`, { params: { ...q, refresh: refresh ? 1 : undefined } }); if (id === reqRef.current) setData({ ...r.data, _kind: kind }); }
    catch (e) { if (id === reqRef.current) { setData(null); toast.error(errMsg(e, "Rapport QuickBooks indisponible")); } }
    finally { if (id === reqRef.current) setLoading(false); if (inflightRef.current === key) inflightRef.current = ""; }
  }, [query, masterId, kind, p.start, p.end]);
  useEffect(() => { load(); }, [load]);

  const pdf = () => downloadPdf(`${qboBase(masterId)}/reports/${kind}/pdf?${new URLSearchParams(query()).toString()}`, `QuickBooks_${kind}_${p.end}.pdf`, setBusy);

  if (!cat || !def) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  return (
    <div className="space-y-4" data-testid="qbo-reports-tab">
      <div className="flex flex-wrap gap-x-6 gap-y-2" data-testid="qbo-report-groups">
        {Object.entries(groups).map(([label, items]) => (
          <div key={label} className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{label}</span>
            <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden flex-wrap">
              {items.map((r) => (
                <button key={r.id} onClick={() => setKind(r.id)} className={`px-3 py-1.5 text-xs font-semibold transition ${kind === r.id ? "bg-[#2CA01C] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`qbo-report-kind-${r.id}`}>{r.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 bg-white border rounded-xl px-3 py-2">
        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
          <span className="font-bold text-slate-800 text-sm mr-2">{def.label}</span>
          {def.mode === "period" && <>Du <input type="date" value={p.start} onChange={(e) => setP({ ...p, start: e.target.value })} className={dateCls} data-testid="qbo-report-start" /> au</>}
          {def.mode !== "period" && <>Au</>}
          <input type="date" value={p.end} onChange={(e) => setP({ ...p, end: e.target.value })} className={dateCls} data-testid="qbo-report-end" />
          {def.method && (
            <select value={p.method} onChange={(e) => setP({ ...p, method: e.target.value })} className={dateCls} data-testid="qbo-report-method">
              <option value="Accrual">Comptabilité d'exercice</option><option value="Cash">Comptabilité de caisse</option>
            </select>
          )}
          {def.columns && (
            <select value={p.columns} onChange={(e) => setP({ ...p, columns: e.target.value })} className={dateCls} data-testid="qbo-report-columns">
              {COLUMNS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          )}
          {def.party && <PartySelect masterId={masterId} kind={def.party} value={p.party} onChange={(v) => setP({ ...p, party: v })} />}
          {kind === "tax-summary" && cat.tax_agencies?.length > 0 && (
            <select value={p.agency} onChange={(e) => setP({ ...p, agency: e.target.value })} className={dateCls} data-testid="qbo-report-agency">
              <option value="">Toutes les agences (TPS + TVQ)</option>
              {cat.tax_agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data?._kind === kind && <span className="text-[11px] text-slate-400" data-testid="qbo-report-fetched">{data.stale ? "Dernier instantané" : data.cached ? "Lu il y a moins de 15 min" : "Lu chez Intuit"} · {fmtDateTime(data.fetched_at)}</span>}
          <Btn tone="slate" icon={RefreshCw} onClick={() => load(true)} busy={loading} testId="qbo-report-refresh-btn" title="Relire maintenant chez QuickBooks">Relire</Btn>
          <Btn tone="slate" icon={FileDown} busy={busy} disabled={!data || data._kind !== kind} onClick={pdf} testId="qbo-report-pdf-btn">PDF</Btn>
        </div>
      </div>

      {data?._kind === kind && data.stale && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-xs text-amber-900 flex items-center gap-2" data-testid="qbo-report-stale">
          <AlertTriangle className="w-4 h-4 shrink-0" /> QuickBooks est indisponible ({data.stale_reason}) — affichage du dernier instantané lu le {fmtDateTime(data.fetched_at)}.
        </div>
      )}
      {loading && !data && <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
      {data?._kind === kind && data.parts.map((part, i) => (
        <Card key={i} title={part.label} right={part.header?.basis ? `Méthode : ${part.header.basis === "Cash" ? "caisse" : "exercice"}${part.header.currency ? ` · ${part.header.currency}` : ""}` : part.header?.currency} testId={`qbo-report-part-${i}`}>
          <QboReportTable part={part} testId={`qbo-report-table-${i}`} />
        </Card>
      ))}
    </div>
  );
}
